/**
 * MicroCarePriceAdmin — inline pricing / catalog editor for MicroCare SKUs
 * (Sterile Processing / Instrument Reprocessing).
 *
 * Mirrors the Sol-M admin page but uses the flat `microcare_prices` collection.
 * Super-admin / owner only (backend enforces).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Save, Trash2, DollarSign, Filter } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("sterilemate_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function num(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function Row({ row, onSaved }) {
  const [unit, setUnit] = useState(row.unit_price ?? "");
  const [caseP, setCaseP] = useState(row.case_price ?? "");
  const [uom, setUom] = useState(row.uom ?? "");
  const [upc, setUpc] = useState(row.units_per_case ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setUnit(row.unit_price ?? "");
    setCaseP(row.case_price ?? "");
    setUom(row.uom ?? "");
    setUpc(row.units_per_case ?? "");
  }, [row.unit_price, row.case_price, row.uom, row.units_per_case]);

  const dirty =
    String(unit ?? "") !== String(row.unit_price ?? "") ||
    String(caseP ?? "") !== String(row.case_price ?? "") ||
    String(uom ?? "") !== String(row.uom ?? "") ||
    String(upc ?? "") !== String(row.units_per_case ?? "");

  const effUpc = num(upc) || row.units_per_case || null;
  const preview = useMemo(() => {
    const u = num(unit);
    const c = num(caseP);
    if (u && !c && effUpc) return { unit: u, case: +(u * effUpc).toFixed(2) };
    if (c && !u && effUpc) return { unit: +(c / effUpc).toFixed(4), case: c };
    return { unit: u, case: c };
  }, [unit, caseP, effUpc]);

  const save = async () => {
    const payload = {};
    const u = num(unit);
    const c = num(caseP);
    if (u !== null) payload.unit_price = u;
    if (c !== null) payload.case_price = c;
    if (uom !== row.uom) payload.uom = uom || null;
    const upcN = num(upc);
    if (upcN !== null || upc === "") payload.units_per_case = upcN;
    if (Object.keys(payload).length === 0) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/microcare/products/${encodeURIComponent(row.sku)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).detail || `HTTP ${res.status}`);
      const data = await res.json();
      toast.success(`Saved ${row.sku}`);
      onSaved?.(row.sku, data.row);
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <tr className={row.has_price ? "" : "bg-amber-50/40"} data-testid={`mc-row-${row.sku}`}>
      <td className="p-2 align-top">
        <p className="text-[11px] font-mono text-slate-500">{row.sku}</p>
        <p className="text-sm font-semibold text-slate-900 leading-snug">{row.product_name}</p>
        <p className="text-xs text-slate-500">{row.description}</p>
        <p className="text-[10px] text-slate-400 mt-1">
          {row.uom} · {row.units_per_case ? `${row.units_per_case}/case` : "—"} · {row.category}
        </p>
      </td>
      <td className="p-2 align-top w-32">
        <div className="relative">
          <DollarSign className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input type="number" step="0.0001" min="0" value={unit}
            placeholder={preview.unit ? `~ ${preview.unit}` : "unit"}
            onChange={(e) => setUnit(e.target.value)}
            className="pl-6 h-8 text-sm" data-testid={`mc-unit-${row.sku}`} />
        </div>
      </td>
      <td className="p-2 align-top w-32">
        <div className="relative">
          <DollarSign className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input type="number" step="0.01" min="0" value={caseP}
            placeholder={preview.case ? `~ ${preview.case}` : "case"}
            onChange={(e) => setCaseP(e.target.value)}
            className="pl-6 h-8 text-sm" data-testid={`mc-case-${row.sku}`} />
        </div>
      </td>
      <td className="p-2 align-top w-32 text-xs text-slate-500">
        {row.updated_at ? new Date(row.updated_at).toLocaleDateString() : "—"}
        <div className="truncate text-[10px]" title={row.updated_by}>{row.updated_by}</div>
      </td>
      <td className="p-2 align-top text-right whitespace-nowrap">
        <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid={`mc-save-${row.sku}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
        </Button>
      </td>
    </tr>
  );
}

export default function MicroCarePriceAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [unpricedOnly, setUnpricedOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/microcare/products${unpricedOnly ? "?unpriced_only=true" : ""}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.rows || []);
    } catch (e) { toast.error(`Load failed: ${e.message}`); }
    finally { setLoading(false); }
  }, [unpricedOnly]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r =>
      r.sku?.toLowerCase().includes(s) ||
      r.product_name?.toLowerCase().includes(s) ||
      r.description?.toLowerCase().includes(s) ||
      r.category?.toLowerCase().includes(s)
    );
  }, [q, rows]);

  const onSaved = (sku, patch) =>
    setRows(prev => prev.map(r => r.sku === sku ? { ...r, ...patch, has_price: !!(patch.unit_price || patch.case_price) } : r));

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="microcare-price-admin">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-slate-900">MicroCare — Sterile Processing Pricing</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            {rows.length} SKUs across {new Set(rows.map(r => r.category)).size} categories. Prices seeded from the 2026 MicroCare distributor sheet. Edit unit or case price; missing side auto-computed from <code>units_per_case</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setUnpricedOnly(v => !v)}
            className={`text-xs font-semibold px-3 py-2 rounded-lg border inline-flex items-center gap-1.5 ${unpricedOnly ? "bg-amber-100 border-amber-400 text-amber-900" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}
            data-testid="mc-toggle-unpriced">
            <Filter className="w-3.5 h-3.5" /> {unpricedOnly ? "Unpriced only" : "All SKUs"}
          </button>
        </div>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU, product, category…" className="pl-9" data-testid="mc-search" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                <th className="p-2">SKU · Product · Description</th>
                <th className="p-2 w-32">Unit</th>
                <th className="p-2 w-32">Case</th>
                <th className="p-2 w-32">Last update</th>
                <th className="p-2 w-24 text-right">Save</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={5} className="p-10 text-center text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading MicroCare…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="p-10 text-center text-slate-500">No matching SKUs.</td></tr>
              ) : filtered.map(r => <Row key={r.sku} row={r} onSaved={onSaved} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
